import { Link } from "react-router-dom";
import { MapPin, ChevronRight, Trophy } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useSettings } from "@/context/SettingsContext";
import { formatDate } from "@/lib/format";

const TYPE_COLORS = {
  FIA: "text-[#007AFF] border-[#007AFF]/40",
  WSK: "text-[#FF3B30] border-[#FF3B30]/40",
  COTF: "text-[#FFB800] border-[#FFB800]/40",
  NATIONAL: "text-[#10B981] border-[#10B981]/40",
};

const EventBest = ({ best }) => {
  const { t } = useT();
  return (
    <div data-testid="event-row-best" className="min-w-0 sm:min-w-[130px] border border-[#10B981]/40 bg-[#10B981]/10 rounded-sm px-3 py-2">
      <div className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-[#10B981]"><Trophy className="w-3 h-3" /> {t("event_best")}</div>
      <div data-testid="event-row-best-lap" className="font-heading font-extrabold text-2xl leading-none text-[#10B981] tabular-nums mt-1">{best?.lap || "—"}</div>
      <div data-testid="event-row-best-driver" className="font-mono text-[10px] uppercase text-slate-300 mt-1 max-w-[130px] truncate">{best ? best.driver || t("no_driver") : " "}</div>
    </div>
  );
};

const DriverLaps = ({ event }) => {
  const { t } = useT();
  const rows = event.driver_laps || [];
  return (
    <div data-testid="event-row-total-laps" className="min-w-0 sm:min-w-[190px] border border-[#FFB800]/40 bg-[#FFB800]/10 rounded-sm px-3 py-2">
      <div className="flex items-center justify-between gap-2 font-mono text-[9px] uppercase tracking-widest text-[#FFB800]">
        <span>{t("laps_per_driver")}</span><span className="text-slate-400">{t("total")} {event.total_laps ?? 0}</span>
      </div>
      {rows.length === 0 && <div className="font-mono text-xs text-slate-500 mt-1">—</div>}
      {rows.slice(0, 3).map((d) => (
        <div key={d.driver || "_none"} data-testid={`event-row-driver-laps-${d.driver || "none"}`} className="flex items-baseline justify-between gap-3 mt-1">
          <span className="font-mono text-[11px] uppercase text-slate-200 truncate max-w-[110px]">{d.driver || t("no_driver")}</span>
          <span className="font-heading font-extrabold text-xl leading-none text-[#FFB800] tabular-nums">{d.laps}</span>
        </div>
      ))}
      {rows.length > 3 && <div className="font-mono text-[10px] text-slate-500 mt-1">+{rows.length - 3} {t("drivers")}</div>}
    </div>
  );
};

const todayIso = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};
export const isLive = (ev) => { const d = todayIso(); return ev.start_date <= d && d <= (ev.end_date || ev.start_date); };

const LiveBadge = () => (
  <span data-testid="event-row-live" className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#FF3B30] text-white">
    <span className="relative flex w-1.5 h-1.5"><span className="absolute inset-0 rounded-full bg-white animate-ping opacity-75" /><span className="relative w-1.5 h-1.5 rounded-full bg-white" /></span>
    LIVE
  </span>
);

export const EventRow = ({ event, index }) => {
  const { t, lang } = useT();
  const live = isLive(event);
  const hideCat = useSettings().settings.hide_categories;
  const d = new Date(`${event.start_date}T00:00:00`);
  const typeLabel = t(`type_${event.event_type}`) + (event.event_type === "NATIONAL" && event.country ? ` · ${event.country}` : "");
  return (
    <Link
      to={`/events/${event.id}`}
      data-testid={`event-row-${event.id}`}
      style={{ animationDelay: `${Math.min(index, 10) * 40}ms` }}
      className={`fade-up group grid grid-cols-[64px_1fr] sm:grid-cols-[80px_1fr_auto_auto] lg:grid-cols-[80px_1fr_auto_auto_auto] items-center gap-4 bg-[#12141C] border rounded-sm p-3 sm:p-4 hover:border-[#FF3B30]/60 hover:bg-[#181B26] transition-colors duration-200 ${live ? "border-[#FF3B30]/70 shadow-[inset_3px_0_0_#FF3B30]" : "border-[#282D3D]"}`}
    >
      <div className="text-center border-r border-[#282D3D] pr-3">
        <div className="font-heading font-extrabold text-3xl sm:text-4xl leading-none">{String(d.getDate()).padStart(2, "0")}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
          {d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { month: "short", year: "2-digit" })}
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {live && <LiveBadge />}
          <span data-testid="event-row-type" className={`font-mono text-[10px] uppercase tracking-wider border px-1.5 py-0.5 rounded-sm ${TYPE_COLORS[event.event_type] || "text-slate-300 border-slate-600"}`}>
            {typeLabel}
          </span>
          {!hideCat && event.categories?.length > 0 && <span data-testid="event-row-categories" className="font-mono text-[10px] uppercase text-slate-500">{event.categories.join(" · ")}</span>}
          {event.drivers?.length > 0 && <span data-testid="event-row-drivers" className="font-mono text-[10px] text-slate-500">· {event.drivers.length} {event.drivers.length === 1 ? t("driver") : t("drivers")}</span>}
        </div>
        <h3 data-testid="event-row-name" className="font-heading font-bold uppercase text-xl sm:text-2xl leading-tight mt-1 truncate group-hover:text-white">{event.name}</h3>
        <div className="flex items-center gap-1.5 text-sm text-slate-400 mt-0.5">
          <MapPin className="w-3.5 h-3.5" />
          <span data-testid="event-row-track" className="truncate">{event.track}</span>
          {event.end_date && event.end_date !== event.start_date && (
            <span className="hidden md:inline font-mono text-xs text-slate-500 ml-2">
              {formatDate(event.start_date, lang)} → {formatDate(event.end_date, lang)}
            </span>
          )}
        </div>
      </div>
      <div className="hidden lg:block text-right">
        <div data-testid="event-row-session-count" className="font-mono font-bold text-2xl text-white">{event.session_count}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("sessions_count")}</div>
      </div>
      <div className="col-span-2 sm:col-span-1 grid grid-cols-2 sm:flex gap-2 sm:order-none order-last">
        <EventBest best={event.best} />
        <DriverLaps event={event} />
      </div>
      <ChevronRight className="hidden sm:block w-5 h-5 text-slate-600 group-hover:text-[#FF3B30] group-hover:translate-x-0.5 transition-transform duration-200" />
    </Link>
  );
};
